import { ILoanSummaryDashboardClosedLoanTypeSummary } from '@apply/api/src/api/loanApi';
import { TabularGrid } from '@apply/components';
import React from 'react';
import { Column, Summary, TotalItem } from 'devextreme-react/data-grid';
import { Toolbar } from './components/Toolbar/Toolbar';
import { getDashboardLoanTypeName } from '../../../constants/getDashboardLoanTypeName';
import { DashboardClosedByMonthPivotMode } from '../../../constants/DashboardClosedByMonthPivotMode';

export const DashboardClosedByMonth: React.FC<{
    pivotMode: DashboardClosedByMonthPivotMode;
    set_pivotMode: (mode: DashboardClosedByMonthPivotMode) => void;
    dataSource?: ILoanSummaryDashboardClosedLoanTypeSummary[];
}> = ({ pivotMode, set_pivotMode, dataSource = [] }) => {
    const tabularColumns: string[] = ['loanTypeId', 'committedAmount', 'fundedAmount', 'number'];
    const deemphasizedColumns: string[] = [
        'MTD',
        ...(dataSource[0]?.monthlyDetails?.filter((md) => md.isT12 === false)?.map((md) => md.month) ?? []),
    ];

    const columnValueFormat = pivotMode === DashboardClosedByMonthPivotMode.Committed ? 'currency' : undefined;

    return (
        <div
            id='dashboardClosedByMonthGrid'
            className='loanSummaryDashboard dashboardClosedByMonthGrid'
        >
            <Toolbar
                pivotMode={pivotMode}
                set_pivotMode={set_pivotMode}
            />

            <TabularGrid
                dataSource={dataSource}
                keyExpr='loanTypeId'
                defaultPageSize={10}
                pagerVisible={false}
                showBorders={true}
                showRowLines={true}
                rowAlternationEnabled={true}
                remoteOperations={false}
                tabularColumns={tabularColumns}
                noDataText='No Items Found'
                deemphasizedColumns={deemphasizedColumns}
            >
                <Column
                    dataField='loanTypeId'
                    caption=''
                    width={140}
                    alignment='left'
                    cellRender={(e) => getDashboardLoanTypeName(e.data.loanTypeId)}
                />
                <Column
                    dataField='committedAmount'
                    caption='Committed'
                    format='currency'
                    alignment='left'
                    width={120}
                    calculateCellValue={(row) => row.t12Totals.committedAmount}
                />
                <Column
                    dataField='number'
                    caption='Number'
                    alignment='left'
                    width={120}
                    calculateCellValue={(row) => row.t12Totals.number}
                />
                <Column
                    caption='MTD'
                    key='MTD'
                    dataField='MTD'
                    calculateCellValue={(row) =>
                        pivotMode == DashboardClosedByMonthPivotMode.Committed
                            ? row.monthToDate.committedAmount
                            : row.monthToDate.number
                    }
                    format={columnValueFormat}
                    alignment='left'
                    width={120}
                />

                {dataSource[0]?.monthlyDetails.map((m, idx) => (
                    <Column
                        key={m.month}
                        caption={m.month}
                        dataField={m.month}
                        width={100}
                        alignment='left'
                        format={columnValueFormat}
                        calculateCellValue={(row) =>
                            pivotMode == DashboardClosedByMonthPivotMode.Committed
                                ? row.monthlyDetails[idx].counts.committedAmount
                                : row.monthlyDetails[idx].counts.number
                        }
                    />
                ))}

                <Summary
                    calculateCustomSummary={(options) => {
                        if (options.name !== 'mtdSummary') return;

                        if (options.summaryProcess === 'start') {
                            options.totalValue = 0;
                        }

                        if (options.summaryProcess === 'calculate') {
                            const val =
                                pivotMode === DashboardClosedByMonthPivotMode.Committed
                                    ? options.value.monthToDate.committedAmount
                                    : options.value.monthToDate.number;

                            options.totalValue += val;
                        }
                    }}
                >
                    <TotalItem
                        column='loanTypeId'
                        summaryType='count'
                        displayFormat='Total'
                        showInColumn='loanTypeId'
                    />

                    <TotalItem
                        column='committedAmount'
                        summaryType='sum'
                        valueFormat='currency'
                        displayFormat='{0}'
                    />

                    <TotalItem
                        column='fundedAmount'
                        summaryType='sum'
                        valueFormat='currency'
                        displayFormat='{0}'
                    />
                    <TotalItem
                        column='number'
                        summaryType='sum'
                        displayFormat='{0}'
                    />
                    <TotalItem
                        name='mtdSummary'
                        summaryType='custom'
                        showInColumn='MTD'
                        displayFormat='{0}'
                        valueFormat={columnValueFormat}
                    />

                    {dataSource[0]?.monthlyDetails.map((m, idx) => (
                        <TotalItem
                            key={m.month}
                            summaryType='sum'
                            displayFormat='{0}'
                            valueFormat={columnValueFormat}
                            column={m.month}
                        />
                    ))}
                </Summary>
            </TabularGrid>
        </div>
    );
};
